import { Button, FormControl, FormErrorMessage, Link, Stack, VStack, useToast } from '@chakra-ui/react';
import { useUserContext } from '@providers/useUserContext';
import FloatingLabelInput from 'components/FloatingLabelInput';
import { t } from 'i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import { ApiClientExceptionErrorCode } from 'types/api';
import { ApiClientException } from 'utils/apiClientException';
import { HTTP } from 'utils/http';
import { resolveUserError } from 'utils/userErrorMessages';
import('react-phone-number-input/style.css');

interface LoginFormValues {
  email: string;
  password: string;
}

export function Login() {
  const { state: locationState } = useLocation();
  const navigate = useNavigate();
  const toast = useToast({ id: 'migration' });
  const methods = useForm<LoginFormValues>({});
  const {
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = methods;
  const { login } = useUserContext();

  async function onSubmit({ email, password }: LoginFormValues) {
    try {
      await login(email, password);
      if (locationState && locationState.onLoginRedirect) {
        navigate(locationState.onLoginRedirect, { state: null });
      }
    } catch (error) {
      if (error instanceof ApiClientException) {
        const message = error.getMessage();
        if (error.ErrorCode === ApiClientExceptionErrorCode.ClientMissingFromSite) {
          try {
            toast({ title: t("Votre compte client n'existe pas encore sur cette marque, nous allons le migrer") });
            await HTTP.post('/auth/migrate-user', { email }).send();
            toast({ title: t('Migration terminée'), status: 'success' });
            await login(email, password);
            return;
          } catch (migrationError) {
            if (migrationError instanceof ApiClientException) {
              return setError('root', { message: migrationError.getMessage() });
            }

            const { title, description } = resolveUserError('auth', migrationError);
            return setError('root', { message: description ? `${title}. ${description}` : title });
          }
        }

        if (error.ErrorCode === ApiClientExceptionErrorCode.ClientNotFound) {
          const title = t('errors.context.auth.clientNotFound.title');
          const description = t('errors.context.auth.clientNotFound.description');
          setError('root', { message: `${title}. ${description}` });
          return;
        }

        if (error.ErrorCode === ApiClientExceptionErrorCode.InvalidPassword) {
          setError('password', { message });
        } else {
          setError('root', { message });
        }
        return;
      }

      const { title, description } = resolveUserError('auth', error);
      setError('root', { message: description ? `${title}. ${description}` : title });
    }
  }

  return (
    <Stack gap={10}>
      <FormProvider {...methods}>
        <FormControl isInvalid={!!errors.root}>
          <VStack className="loginForm" align="stretch" as="form" onSubmit={handleSubmit(onSubmit)}>
            <FormControl isInvalid={!!errors.email}>
              <FloatingLabelInput
                name="email"
                label="E-mail"
                type="email"
                registerOptions={{ required: 'Veuillez entrer un email valide' }}
                errorMessage="Veuillez entrer un email valide"
              />
            </FormControl>

            <FormControl isInvalid={!!errors.password}>
              <FloatingLabelInput
                name="password"
                label="Mot de passe"
                type="password"
                registerOptions={{ required: 'Veuillez entrer un mot de passe valide' }}
                errorMessage="Veuillez entrer un mot de passe valide"
              />
              <FormErrorMessage alignSelf="center" justifyContent={'center'}>
                {errors.root?.message}
              </FormErrorMessage>
            </FormControl>
            <FormErrorMessage alignSelf="center" justifyContent={'center'}>
              {errors.root?.message}
            </FormErrorMessage>
            <VStack padding="2em 1em">
              <Button
                type="submit"
                textAlign="center"
                alignContent={'center'}
                margin="auto"
                isLoading={isSubmitting}
                variant="contained"
              >
                Me connecter
              </Button>
              <Link color="blue.500" onClick={() => navigate('/account/reset-password')} style={{ marginTop: '1em' }}>
                Mot de passe oublié?
              </Link>
            </VStack>
          </VStack>
        </FormControl>
      </FormProvider>
    </Stack>
  );
}
